/**
 * Items de choix de langue — `MENU_RADIO` du contrat, dans un `role="menu"`.
 *
 * Partagés entre le menu du compte (où la langue vit une fois connecté, coquille.md §6.3)
 * et le déroulant de langue des pages publiques (connexion, invitation, partage) : la même
 * liste, la même coche carrée, le même comportement de persistance.
 *
 * Les noms restent des endonymes non traduits : « Français », « English ». Une langue se
 * nomme toujours elle-même dans un sélecteur de langue.
 *
 * `i18n.resolvedLanguage` (jamais `i18n.language` seul) : ce dernier conserve la région
 * détectée (`fr-FR`), le premier est déjà réduit à la langue de base par `load: 'languageOnly'`.
 *
 * Le profil est la source de vérité de la langue (chantier préférences, 2026-09-15) :
 * `i18next` décidait seul depuis `localStorage`, ce qui divergeait du réglage enregistré sur
 * `ProfilePage`. `persist` (posé par `MenuCompte`, seul appelant qui a la garantie d'une
 * session ouverte) fait persister le choix côté serveur (`PATCH /me`) en plus du changement
 * local immédiat. Volontairement PAS un `useSession()` ici : les pages publiques qui montent
 * `MenuLangue` (connexion, invitation, partage) n'ont pas toujours de `SessionProvider` dans
 * leur arbre de test, et le composant ne doit dépendre que de ce que son appelant lui donne.
 */

import { useTranslation } from 'react-i18next';
import { LOCALES } from '@exiplan/shared-types';

import { api } from '../../lib/api.js';
import { MENU_CHECK_OFF, MENU_CHECK_ON, MENU_RADIO } from '../../ui/theme.js';

/*
 * Endonymes, dans l'ordre de `LOCALES` du contrat partagé — pas une liste parallèle. C'est la
 * même énumération qui borne `users.locale` côté serveur et `supportedLngs` côté i18next :
 * trois listes qui divergent, c'est une langue acceptée en base qu'aucune ressource ne sait
 * afficher. Les noms ne sont JAMAIS traduits (on écrit « Français », pas « French »).
 */
const ENDONYMES = { fr: 'Français', en: 'English' };

export const LANGUES = LOCALES.map((code) => ({ code, nom: ENDONYMES[code] ?? code }));

export function langueActive(i18n) {
  return i18n.resolvedLanguage || i18n.language?.split('-')[0] || 'fr';
}

export function ChoixLangue({ persist = false }) {
  const { i18n } = useTranslation();
  const active = langueActive(i18n);

  const choisir = async (code) => {
    await i18n.changeLanguage(code);
    if (!persist) return;
    try {
      await api.patch('/me', { locale: code });
    } catch {
      // Le changement reste appliqué localement même si la persistance échoue : la langue
      // affichée ne doit jamais dépendre de la disponibilité du réseau.
    }
  };

  return LANGUES.map(({ code, nom }) => {
    const choisi = active === code;
    return (
      <button
        key={code}
        type="button"
        role="menuitemradio"
        aria-checked={choisi}
        lang={code}
        className={MENU_RADIO}
        onClick={() => choisir(code)}
      >
        {nom}
        <span className={choisi ? MENU_CHECK_ON : MENU_CHECK_OFF} aria-hidden="true" />
      </button>
    );
  });
}
